import React, { useState, useEffect } from 'react';
import { Plus, MapPin, Users, Clock, Settings, Edit, Trash2, Building, Building2, Navigation, Map, Eye, Layers, Calendar, User, Globe, Target, CheckCircle, AlertCircle, Info, Home, Factory, Warehouse, Hammer, Wrench, TreePine, X, Download, Upload, ChevronDown, ChevronUp, ChevronRight, Construction } from 'lucide-react';
import { getSiteTypeIcon, getSiteTypeColor, getSiteTypeLabel, siteTypeOptions } from '../config/siteTypeConfig';
import api from '../services/api';
import { usePersistence } from '../hooks/usePersistence';
import SiteForm from '../components/Forms/SiteForm';
import WorkSiteForm from '../components/Forms/WorkSiteForm';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import MapComponent from '../components/UI/MapComponent';
import PostalCodeLookup from '../components/UI/PostalCodeLookup';
import GeocodingService from '../services/geocoding';
import Portal from '../components/UI/Portal';
import toast from '../utils/toast';
import ConfirmModal from '../components/UI/ConfirmModal';
import { StatusBadge } from '../components/UI/StatusBadge';
import { Pagination } from '../components/UI/Pagination';
import { FilterButton } from '../components/UI/FilterButton';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import PillSelect from '../components/UI/PillSelect';
import PillToggle from '../components/UI/PillToggle';
import FormSelect from '../components/UI/FormSelect';
import { getTodayInSingaporeTimezone, formatDateForInput, formatDate } from '../utils/dateUtils';
import { useIsMobile } from '../hooks/useIsMobile';

// Helper function to safely format coordinates
const formatCoordinate = (coord, decimals = 4) => {
  if (coord === null || coord === undefined || coord === '') return null;
  const num = parseFloat(coord);
  if (isNaN(num)) return null;
  return num.toFixed(decimals);
};

const SiteManagement = () => {
  const [projects, setProjects] = useState([]);
  const [workSites, setWorkSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Persistent filter states - using individual calls to avoid Rules of Hooks violation
  const [searchTerm, setSearchTerm] = usePersistence('site-management-searchTerm', '');
  const [filterStatus, setFilterStatus] = usePersistence('site-management-filterStatus', '');
  const [projectDetailsTypeFilter, setProjectDetailsTypeFilter] = usePersistence('site-management-projectDetailsTypeFilter', '');
  const [filterLocationType, setFilterLocationType] = usePersistence('site-management-filterLocationType', '');
  
  // Persistent UI preferences
  const [viewMode, setViewMode] = usePersistence('site-management-viewMode', 'grid');
  const [showFilters, setShowFilters] = usePersistence('site-management-showFilters', false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;
  
  // Non-persistent states (modal states, selections)
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Modal states
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [showWorkSiteForm, setShowWorkSiteForm] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const [showProjectMapModal, setShowProjectMapModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showWorkSiteMapModal, setShowWorkSiteMapModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingWorkSite, setEditingWorkSite] = useState(null);
  const [selectedWorkSite, setSelectedWorkSite] = useState(null);
  const [selectedCoordinate, setSelectedCoordinate] = useState(null);

  // Statistics state
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalWorkSites: 0,
    activeWorkSites: 0,
    // Additional stats for tender API coordinates
    projectsWithCoordinates: 0,
    totalCoordinates: 0
  });

  useEffect(() => {
    // Debug API configuration
    console.log('🔍 SiteManagement Component Loaded');
    console.log('🔍 API object:', api);
    console.log('🔍 API baseURL:', api.defaults?.baseURL || 'Not available');
    
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch projects and ALL work sites (including inactive) so admin can manage them.
      // Newest-created first so HR can immediately configure a project they just added.
      const [projectsResponse, workSitesResponse] = await Promise.all([
        api.get('/projects/', { params: { page_size: 1000, ordering: '-project_created_at' } }),
        api.get('/work-sites/', { params: { page_size: 1000 } })
      ]);

      // Debug logging to see actual API responses
      console.log('🔍 Projects API Response:', projectsResponse);
      console.log('🔍 Work Sites API Response:', workSitesResponse);
      console.log('🔍 Work Sites Data:', workSitesResponse.data);

      // Handle different response formats for work sites
      const workSitesData = Array.isArray(workSitesResponse.data) 
        ? workSitesResponse.data 
        : (workSitesResponse.data?.results || []);
      
      const projectsDataRaw = Array.isArray(projectsResponse.data)
        ? projectsResponse.data
        : (projectsResponse.data?.results || []);
      console.log('🔍 Projects Count:', projectsDataRaw.length);
      console.log('🔍 Work Sites Response Structure:', workSitesResponse);
      console.log('🔍 Work Sites Data Type:', typeof workSitesResponse.data);
      console.log('🔍 Work Sites Data Keys:', Object.keys(workSitesResponse.data || {}));
      if (workSitesData.length > 0) {
        console.log('🔍 Sample Work Site Structure:', workSitesData[0]);
        console.log('🔍 Sample Work Site Fields:', Object.keys(workSitesData[0]));
      }

      // Use backend projects directly
      const projectsData = projectsDataRaw;

      console.log('🔍 Final Mapped Projects Data:', projectsData);
      console.log('🔍 Final Work Sites Data:', workSitesData);

      setProjects(projectsData);
      setWorkSites(workSitesData);

      // Calculate statistics
      const mappedWorkSites = workSitesData.filter(ws => ws.site_latitude && ws.site_longitude);
      setStats({
        totalProjects: projectsData.length,
        activeProjects: projectsData.filter(p => p.project_status === 'active').length,
        totalWorkSites: workSitesData.length,
        activeWorkSites: workSitesData.filter(w => w.site_status === 'active').length,
        // Count projects that have at least one work site with coordinates
        projectsWithCoordinates: projectsData.filter(p => 
          workSitesData.some(ws => 
            ws.project_code === p.project_code && 
            ws.site_latitude && 
            ws.site_longitude
          )
        ).length,
        // Count total work sites with coordinates
        totalCoordinates: mappedWorkSites.length
      });

    } catch (error) {
      console.error('❌ Error loading data:', error);
      console.error('❌ Error details:', error.message);
      toast.error(`Failed to load project data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter projects based on search, status, and location type.
  // Keep newest-created first after filtering (HR setup flow: create → configure sites).
  const filteredProjects = projects
    .filter(project => {
      const matchesSearch = project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.project_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (project.project_client_name && project.project_client_name.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = filterStatus === '' || filterStatus === 'all' || project.project_status === filterStatus;

      // Filter by location type if specified
      let matchesLocationType = true;
      if (filterLocationType && filterLocationType !== '') {
        const projectWorkSites = getWorkSitesForProject(project.project_code);
        matchesLocationType = projectWorkSites.some(site => site.site_type === filterLocationType);
      }

      return matchesSearch && matchesStatus && matchesLocationType;
    })
    .sort((a, b) => {
      const aTime = a.project_created_at ? new Date(a.project_created_at).getTime() : 0;
      const bTime = b.project_created_at ? new Date(b.project_created_at).getTime() : 0;
      if (bTime !== aTime) return bTime - aTime;
      return (a.project_code || '').localeCompare(b.project_code || '');
    });

  // Pagination calculations
  const totalCount = filteredProjects.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedProjects = filteredProjects.slice(startIndex, startIndex + PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Get work sites for selected project
  const getWorkSitesForProject = (projectCode) => {
    return workSites.filter(site => site.project_code === projectCode);
  };

  // Handler functions
  async function handleSaveProject(projectData) {
    try {
      // Validate required fields before sending
      if (!projectData.project_code || !projectData.project_name) {
        toast.error('❌ Project code and name are required');
        return;
      }
      
      // Clean up the data before sending
      const cleanedData = {
        ...projectData,
        // Ensure boolean fields are proper booleans
        project_requires_checkin: Boolean(projectData.project_requires_checkin),
        // Ensure numeric fields are proper numbers or null with proper precision
        project_latitude: projectData.project_latitude ? 
          parseFloat(parseFloat(projectData.project_latitude).toFixed(10)) : null,
        project_longitude: projectData.project_longitude ? 
          parseFloat(parseFloat(projectData.project_longitude).toFixed(10)) : null,
        project_geofence_radius: parseInt(projectData.project_geofence_radius) || 150,
        // Ensure empty strings are converted to null for optional fields
        project_manager_staff_id: projectData.project_manager_staff_id || null,
        project_start_date: projectData.project_start_date || null,
        project_end_date: projectData.project_end_date || null,
        // Handle coordinates array from tender API structure
        coordinates: projectData.coordinates ? projectData.coordinates.map(coord => ({
          reference_name: coord.reference_name || coord.site_name || 'Main Site',
          latitude: coord.latitude ? parseFloat(parseFloat(coord.latitude).toFixed(8)) : null,
          longitude: coord.longitude ? parseFloat(parseFloat(coord.longitude).toFixed(8)) : null,
          address: coord.address || coord.site_address || null,
          description: coord.description || coord.site_description || null
        })) : []
      };
      
      if (editingProject) {
        await api.put(`/projects/${editingProject.project_code}/`, cleanedData);
        toast.success('✅ Project updated successfully');
      } else {
        await api.post('/projects/', cleanedData);
        toast.success('✅ Project created successfully');
      }
      
      setShowSiteForm(false);
      setEditingProject(null);
      loadData();
    } catch (error) {
      console.error('❌ Error saving project:', error);
      console.error('❌ Error response:', error.response?.data);
      
      // Provide specific error messages based on the response
      let errorMessage = 'Failed to save project';
      
      if (error.response?.status === 403) {
        errorMessage = 'Access denied. You need admin permissions to create/edit projects.';
      } else if (error.response?.status === 400) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'object') {
          // Handle field-specific errors
          const fieldErrors = Object.entries(errorData)
            .map(([field, errors]) => {
              const errorList = Array.isArray(errors) ? errors : [errors];
              return `${field}: ${errorList.join(', ')}`;
            })
            .join('; ');
          errorMessage = `Validation error: ${fieldErrors}`;
        } else {
          errorMessage = `Validation error: ${errorData}`;
        }
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(`❌ ${errorMessage}`);
    }
  }

  function handleDeleteProject(project) {
    setConfirmAction({ type: 'project', project });
  }

  function handleDeleteWorkSiteConfirm(workSite) {
    setConfirmAction({ type: 'worksite', workSite });
  }

  async function handleConfirmDelete() {
    if (!confirmAction) return;
    try {
      if (confirmAction.type === 'project') {
        await api.delete(`/projects/${confirmAction.project.project_code}/`);
        toast.success('✅ Project deleted successfully');
      } else if (confirmAction.type === 'worksite') {
        const response = await api.delete(`/work-sites/${confirmAction.workSite.id}/`);
        toast.success('✅ Work site deleted successfully');
        const tenderSync = response?.data?.tender_sync;
        if (tenderSync?.success) {
          toast.success('🔄 Synced deletion to Tender API', { duration: 2000 });
        }
      }
      loadData();
    } catch (error) {
      if (confirmAction.type === 'project') {
        console.error('Error deleting project:', error);
        toast.error('❌ Failed to delete project');
      } else {
        console.error('Error deleting work site:', error);
        toast.error('❌ Failed to delete work site');
      }
    } finally {
      setConfirmAction(null);
    }
  }

  async function handleSaveWorkSite(workSiteData) {
    try {
      // Prefer payload id (form carries the site being edited) so we never
      // fall through to POST when HR amends an existing location.
      const siteId = workSiteData?.id ?? editingWorkSite?.id ?? null;
      const projectCode =
        workSiteData.project ||
        workSiteData.project_code ||
        selectedProject?.project_code ||
        editingWorkSite?.project ||
        editingWorkSite?.project_code;

      if (!projectCode) {
        toast.error('Project is required for a work site');
        return;
      }

      // Writable fields only — strip list/serializer extras that confuse create/update.
      const payload = {
        project: projectCode,
        site_name: workSiteData.site_name,
        site_description: workSiteData.site_description ?? '',
        site_address: workSiteData.site_address ?? '',
        site_postal_code: workSiteData.site_postal_code ?? '',
        unit_number: workSiteData.unit_number ?? '',
        site_latitude: workSiteData.site_latitude ?? null,
        site_longitude: workSiteData.site_longitude ?? null,
        site_geofence_radius: workSiteData.site_geofence_radius ?? 150,
        site_type: workSiteData.site_type || 'project_site',
        site_status: workSiteData.site_status || 'active',
        site_requires_checkin: Boolean(workSiteData.site_requires_checkin),
        site_is_primary: Boolean(workSiteData.site_is_primary),
        site_access_instructions: workSiteData.site_access_instructions ?? '',
        site_safety_requirements: workSiteData.site_safety_requirements ?? '',
      };

      let savedResponse;
      if (siteId) {
        savedResponse = await api.put(`/work-sites/${siteId}/`, payload);
        toast.success('Work site updated successfully');
      } else {
        savedResponse = await api.post('/work-sites/', payload);
        toast.success('Work site created successfully');
      }

      // Show Tender API sync status feedback
      const tenderSync = savedResponse?.data?.tender_sync;
      if (tenderSync) {
        if (tenderSync.success) {
          toast.success('Synced to Tender API', { duration: 2000 });
        } else if (tenderSync.error) {
          toast.warning(`Tender sync failed: ${tenderSync.error}`, { duration: 4000 });
        }
      }

      // Enforce single primary per project: if this site is primary, unset others
      if (payload.site_is_primary) {
        const saved = savedResponse?.data || editingWorkSite;
        const savedId = saved?.id ?? siteId;
        const primariesToUnset = workSites.filter(
          (ws) => ws.project_code === projectCode && ws.site_is_primary && ws.id !== savedId
        );
        if (primariesToUnset.length > 0) {
          await Promise.all(
            primariesToUnset.map((ws) =>
              api.put(`/work-sites/${ws.id}/`, {
                project: ws.project || ws.project_code,
                site_name: ws.site_name,
                site_description: ws.site_description ?? '',
                site_address: ws.site_address ?? '',
                site_postal_code: ws.site_postal_code ?? '',
                unit_number: ws.unit_number ?? '',
                site_latitude: ws.site_latitude ?? null,
                site_longitude: ws.site_longitude ?? null,
                site_geofence_radius: ws.site_geofence_radius ?? 150,
                site_type: ws.site_type || 'project_site',
                site_status: ws.site_status || 'active',
                site_requires_checkin: Boolean(ws.site_requires_checkin),
                site_is_primary: false,
                site_access_instructions: ws.site_access_instructions ?? '',
                site_safety_requirements: ws.site_safety_requirements ?? '',
              })
            )
          );
        }
      }

      setShowWorkSiteForm(false);
      setEditingWorkSite(null);
      setSelectedProject(null);
      loadData();
    } catch (error) {
      console.error('Error saving work site:', error);
      const errData = error.response?.data;
      const detail =
        errData?.detail ||
        errData?.error ||
        (errData && typeof errData === 'object' && !Array.isArray(errData)
          ? Object.entries(errData)
              .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(', ') : errs}`)
              .join('; ')
          : null) ||
        error.message;
      toast.error(detail ? `Failed to save work site: ${detail}` : 'Failed to save work site');
    }
  }

  // Resolve parent project when opening the work-site form (add or edit).
  function openWorkSiteForm(projectOrCode, workSite = null) {
    const projectCode =
      typeof projectOrCode === 'string'
        ? projectOrCode
        : projectOrCode?.project_code || workSite?.project_code || workSite?.project || null;
    const project =
      (typeof projectOrCode === 'object' && projectOrCode?.project_code
        ? projectOrCode
        : null) ||
      projects.find((p) => p.project_code === projectCode) ||
      (projectCode ? { project_code: projectCode } : null);

    setSelectedProject(project);
    setEditingWorkSite(workSite);
    setShowWorkSiteForm(true);
  }

  // Quick add helpers for common site types (Site Office, DFMA Factory)
  function quickAddWorkSite(project, type) {
    openWorkSiteForm(project, {
      // Draft defaults only — no id → create path (not amend).
      site_type: type,
      site_status: 'active',
      site_geofence_radius: 150,
      site_requires_checkin: true,
      site_is_primary: false,
    });
  }

  // Create work sites from tender API coordinates
  async function createWorkSitesFromCoordinates(project) {
    if (!project.coordinates || project.coordinates.length === 0) {
      toast.error('❌ No coordinates found in project data');
      return;
    }

    try {
      // First, we need to create the project in the HR portal to get a valid project ID
      console.log('🧪 Creating project in HR portal first...');
      
      const projectData = {
        project_code: project.project_code,
        project_name: project.project_name,
        project_description: project.project_description || '',
        project_client_name: project.project_client_name || '',
        project_status: project.project_status || 'active',
        project_requires_checkin: project.project_requires_checkin !== false, // Default to true
        project_geofence_radius: project.project_geofence_radius || 150,
        // Use the actual database fields that were populated from coordinates
        project_latitude: project.project_latitude,
        project_longitude: project.project_longitude,
        project_address: project.project_address || '',
        // Include the coordinates field so it gets processed by the serializer
        coordinates: project.coordinates
      };

      console.log('🧪 Creating project with data:', projectData);
      console.log('🧪 Project data type check:', {
        project_code_type: typeof projectData.project_code,
        project_name_type: typeof projectData.project_name,
        coordinates_type: typeof projectData.coordinates,
        coordinates_length: Array.isArray(projectData.coordinates) ? projectData.coordinates.length : 'not array'
      });

      let hrProjectId;
      try {
        const projectResponse = await api.post('/projects/', projectData);
        console.log('✅ Project created in HR portal:', projectResponse.data);
        hrProjectId = projectResponse.data.id || projectResponse.data.project_code;
      } catch (projectError) {
        console.error('❌ Failed to create project:', projectError.response?.data);
        console.error('❌ Project creation error details:', {
          status: projectError.response?.status,
          data: projectError.response?.data,
          projectData: projectData
        });
        
        // If project already exists, try to find it
        try {
          const existingProjectsResponse = await api.get('/projects/');
          console.log('🔍 Searching for existing project with code:', project.project_code);
          console.log('🔍 Available projects:', existingProjectsResponse.data.results);
          
          const existingProject = existingProjectsResponse.data.results?.find(p => p.project_code === project.project_code);
          if (existingProject) {
            console.log('✅ Found existing project:', existingProject);
            // Use the project_code as the ID since that's the primary key
            hrProjectId = existingProject.project_code;
          } else {
            console.error('❌ Project not found in existing projects');
            toast.error('❌ Failed to create or find project');
            return;
          }
        } catch (findError) {
          console.error('❌ Error finding existing project:', findError);
          toast.error('❌ Failed to find existing project');
          return;
        }
      }

      // Now create work sites using the correct project ID
      console.log('🧪 Creating work sites with project ID:', hrProjectId);
      
      const createdSites = [];
      
      for (const coord of project.coordinates) {
        // Format the work site data according to HR portal API requirements
        const workSiteData = {
          project: hrProjectId, // Use the actual project ID, not the project code
          site_name: coord.reference_name || coord.description || 'Site Location',
          site_description: coord.description || 'Location from project coordinates',
          site_address: coord.address || '',
          site_latitude: coord.latitude ? parseFloat(coord.latitude) : null,
          site_longitude: coord.longitude ? parseFloat(coord.longitude) : null,
          site_geofence_radius: 150,
          site_type: 'project_site',
          site_status: 'active',
          site_requires_checkin: true,
          site_is_primary: coord.description?.toLowerCase().includes('primary') || false
        };

        console.log('🔍 Creating work site with data:', workSiteData);
        console.log('🔍 Work site project field check:', {
          hrProjectId: hrProjectId,
          project_field_type: typeof workSiteData.project,
          project_field_value: workSiteData.project
        });

        try {
          const response = await api.post('/work-sites/', workSiteData);
          console.log('✅ Work site created successfully:', response.data);
          createdSites.push(response.data);
        } catch (error) {
          console.error(`❌ Error creating work site for ${coord.reference_name}:`, error);
          console.error('❌ Error response data:', error.response?.data);
          console.error('❌ Error status:', error.response?.status);
          
          // Show specific error details
          if (error.response?.data) {
            const errorData = error.response.data;
            if (typeof errorData === 'object') {
              const fieldErrors = Object.entries(errorData)
                .map(([field, errors]) => {
                  const errorList = Array.isArray(errors) ? errors : [errors];
                  return `${field}: ${errorList.join(', ')}`;
                })
                .join('; ');
              toast.error(`❌ Validation error: ${fieldErrors}`);
            } else {
              toast.error(`❌ API error: ${errorData}`);
            }
          } else {
            toast.error(`❌ Failed to create work site: ${error.message}`);
          }
        }
      }

      if (createdSites.length > 0) {
        toast.success(`✅ Created ${createdSites.length} work sites from project coordinates`);
        loadData();
      } else {
        toast.error('❌ Failed to create any work sites');
      }
    } catch (error) {
      console.error('Error creating work sites from coordinates:', error);
      toast.error('❌ Failed to create work sites from coordinates');
    }
  }

  // Create a test project with coordinates to verify API integration
  async function createTestProject() {
    try {
      const testProjectData = {
        project_code: `TEST-${Date.now()}`,
        project_name: 'Test Project with Coordinates',
        project_description: 'This is a test project to verify the API integration with coordinates',
        project_client_name: 'Test Client',
        project_status: 'active',
        project_start_date: getTodayInSingaporeTimezone(),
        project_end_date: formatDateForInput(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
        project_contract_sum: '1000000.00',
        project_requires_checkin: true,
        project_geofence_radius: 150,
        // Test coordinates matching the tender API structure
        coordinates: [
          {
            reference_name: 'Main Site',
            latitude: '1.3521',
            longitude: '103.8198',
            address: '123 Business Street, Singapore 123456',
            description: 'Primary project location'
          },
          {
            reference_name: 'Office Building',
            latitude: '1.3522',
            longitude: '103.8199',
            address: '456 Corporate Avenue, Singapore 123457',
            description: 'Secondary office location'
          }
        ]
      };

      console.log('🧪 Creating test project with data:', testProjectData);
      
      const response = await api.post('/projects/', testProjectData);
      console.log('🧪 Test project created:', response.data);
      
      toast.success('✅ Test project created successfully!');
      loadData();
    } catch (error) {
      console.error('❌ Error creating test project:', error);
      console.error('❌ Error response:', error.response?.data);
      
      let errorMessage = 'Failed to create test project';
      if (error.response?.status === 403) {
        errorMessage = 'Access denied. You need admin permissions to create projects.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast.error(`❌ ${errorMessage}`);
    }
  }

  // Quick action: set an existing work site as primary and unset others
  async function handleSetPrimary(workSite) {
    try {
      await api.put(`/work-sites/${workSite.id}/`, { ...workSite, site_is_primary: true });
      const projectCode = workSite.project_code || selectedProject?.project_code;
      const primariesToUnset = workSites.filter(ws => ws.project_code === projectCode && ws.id !== workSite.id && ws.site_is_primary);
      if (primariesToUnset.length > 0) {
        await Promise.all(
          primariesToUnset.map(ws => api.put(`/work-sites/${ws.id}/`, { ...ws, site_is_primary: false }))
        );
      }
      toast.success('✅ Set as primary');
      loadData();
    } catch (error) {
      console.error('Error setting primary:', error);
      toast.error('❌ Failed to set primary site');
    }
  }

  function handleDeleteWorkSite(workSite) {
    setConfirmAction({ type: 'worksite', workSite });
  }

  if (isLoading) {
    return (
      <div className="w-full h-full bg-gray-50 min-h-screen">
        <div className="max-w-full mx-auto">
          {/* Header Skeleton */}
          <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse mr-4"></div>
                <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
              </div>
              <div className="flex items-center">
                <div className="w-5 h-5 bg-gray-200 rounded animate-pulse mr-2"></div>
                <div className="h-5 bg-gray-200 rounded w-96 animate-pulse"></div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
            </div>
          </div>

          {/* Statistics Cards Skeleton */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-8 mb-10">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                <div className="flex items-center animate-pulse">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                  <div className="ml-2 sm:ml-4 space-y-2">
                    <div className="h-6 bg-gray-200 rounded w-12"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Search and Filter Skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border p-6 mb-8">
            <div className="flex items-center justify-between animate-pulse">
              <div className="h-10 bg-gray-200 rounded w-80"></div>
              <div className="flex items-center gap-3">
                <div className="h-10 bg-gray-200 rounded w-32"></div>
                <div className="h-10 bg-gray-200 rounded w-10"></div>
              </div>
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 animate-pulse">
              <div className="flex items-center gap-8">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-4 bg-gray-200 rounded w-24"></div>
                ))}
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="px-6 py-4 animate-pulse">
                  <div className="flex items-center gap-8">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-40"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Building className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Site Management</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Manage projects and work sites for attendance tracking</p>
        </div>

        {/* Statistics Cards - Reduced to 3 (design system compliant) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 mb-8">
          {/* Projects Card */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 sm:w-6 sm:h-6 text-blue-800" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalProjects}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Projects ({stats.activeProjects} active)</p>
              </div>
            </div>
          </div>

          {/* Work Sites Card */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalWorkSites}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Work Sites</p>
              </div>
            </div>
          </div>

          {/* Mapped Projects Card */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Navigation className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.projectsWithCoordinates}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Mapped Projects</p>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar with Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Search — full width on mobile so it's actually usable, capped on desktop */}
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search projects..."
              className="w-full sm:max-w-xl"
            />
            
            {/* Right Actions */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Filter Button */}
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={filterStatus ? 1 : 0}
                onClick={() => setShowFilters(!showFilters)}
              />

              {/* Clear Filters */}
              {(searchTerm || filterStatus) && (
                <button
                  onClick={() => { setSearchTerm(''); setFilterStatus(''); }}
                  className="icon-button-admin"
                  title="Clear filters"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              
              {/* New Project Button */}
              <button
                onClick={() => { setEditingProject(null); setShowSiteForm(true); }}
                className="btn btn-admin-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </button>
            </div>
          </div>

          {/* Collapsible filter panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label-admin">Status</label>
                  <PillSelect
                    value={filterStatus}
                    onChange={setFilterStatus}
                    options={[
                      { value: '', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'on_hold', label: 'On Hold' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' },
                      { value: 'planning', label: 'Planning' },
                    ]}
                    theme="admin"
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Projects Table */}
        <ProjectsTable 
          projects={paginatedProjects}
          onEditProject={(project) => {
            setEditingProject(project);
            setShowSiteForm(true);
          }}
          onDeleteProject={handleDeleteProject}
          onViewProject={(project) => {
            setSelectedProject(project);
            setProjectDetailsTypeFilter('');
            setShowProjectDetails(true);
          }}
          onAddWorkSite={(project) => openWorkSiteForm(project, null)}
          onEditWorkSite={(workSite) =>
            openWorkSiteForm(workSite.project_code || workSite.project, workSite)
          }
          onDeleteWorkSite={handleDeleteWorkSite}
          getWorkSitesForProject={getWorkSitesForProject}
          onSetPrimary={handleSetPrimary}
          onShowWorkSiteMap={(workSite) => {
            setSelectedWorkSite(workSite);
            setShowWorkSiteMapModal(true);
          }}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          theme="admin"
          noun="projects"
          className="mt-6"
        />

        {/* Modals */}
        {showSiteForm && (
          <Portal>
            <div className="modal-overlay">
              <div className="modal-container modal-container-lg max-h-[90vh]">
                <div className="modal-header">
                  <h3 className="modal-title">
                    {editingProject ? 'Edit Project' : 'Add New Project'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowSiteForm(false);
                      setEditingProject(null);
                    }}
                    className="modal-close-admin"
                    title="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="modal-content" style={{ maxHeight: 'calc(95vh - 120px)', overflowY: 'auto' }}>
                  <SiteForm
                    project={editingProject}
                    onClose={() => {
                      setShowSiteForm(false);
                      setEditingProject(null);
                    }}
                    onSave={handleSaveProject}
                    isAdmin={true}
                    isModal={false}
                  />
                </div>
              </div>
            </div>
          </Portal>
        )}

        {showWorkSiteForm && (
          <Portal>
            <WorkSiteFormModal
              key={editingWorkSite?.id != null ? `edit-${editingWorkSite.id}` : 'create'}
              projectCode={
                selectedProject?.project_code ||
                editingWorkSite?.project_code ||
                editingWorkSite?.project ||
                ''
              }
              initialData={editingWorkSite}
              onSubmit={handleSaveWorkSite}
              onCancel={() => {
                setShowWorkSiteForm(false);
                setEditingWorkSite(null);
                setSelectedProject(null);
              }}
            />
          </Portal>
        )}

        {showMapModal && (
          <Portal>
            <MapModal
              projects={projects}
              workSites={workSites}
              onClose={() => setShowMapModal(false)}
            />
          </Portal>
        )}

        {showProjectMapModal && selectedProject && (
          <Portal>
            <ProjectMapModal
              project={selectedProject}
              workSites={getWorkSitesForProject(selectedProject.project_code)}
              selectedCoordinate={selectedCoordinate}
              onClose={() => {
                setShowProjectMapModal(false);
                setSelectedProject(null);
                setSelectedCoordinate(null);
              }}
            />
          </Portal>
        )}

        {showWorkSiteMapModal && selectedWorkSite && (
          <Portal>
            <WorkSiteMapModal
              workSite={selectedWorkSite}
              onClose={() => {
                setShowWorkSiteMapModal(false);
                setSelectedWorkSite(null);
              }}
            />
          </Portal>
        )}

        {showProjectDetails && selectedProject && (
          <Portal>
            <ProjectDetailsModal
              project={selectedProject}
              workSites={getWorkSitesForProject(selectedProject.project_code)}
              initialTypeFilter={projectDetailsTypeFilter}
              onClose={() => {
                setShowProjectDetails(false);
                setSelectedProject(null);
                setProjectDetailsTypeFilter('');
              }}
              onEditProject={() => {
                setEditingProject(selectedProject);
                setShowSiteForm(true);
                setShowProjectDetails(false);
              }}
              onAddWorkSite={() => {
                setShowProjectDetails(false);
                openWorkSiteForm(selectedProject, null);
              }}
              onEditWorkSite={(workSite) => {
                setShowProjectDetails(false);
                openWorkSiteForm(
                  selectedProject || workSite.project_code || workSite.project,
                  workSite
                );
              }}
              onDeleteWorkSite={handleDeleteWorkSite}
              onShowProjectMap={(project, coordinate = null) => {
                setSelectedProject(project);
                setSelectedCoordinate(coordinate);
                setShowProjectMapModal(true);
              }}
              onShowWorkSiteMap={(workSite) => {
                setSelectedWorkSite(workSite);
                setShowWorkSiteMapModal(true);
              }}
              onSetPrimary={handleSetPrimary}
            />
          </Portal>
        )}
      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.type === 'project' ? 'Delete Project' : 'Delete Work Site'}
        message={
          confirmAction?.type === 'project'
            ? `Are you sure you want to delete project "${confirmAction?.project?.project_name}"?`
            : `Are you sure you want to delete work site "${confirmAction?.workSite?.site_name}"?`
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="admin"
      />
      </div>
    </div>
  );
};


// Projects Table Component (replaces grid view)
const ProjectsTable = ({ projects, onEditProject, onDeleteProject, onViewProject, onAddWorkSite, onEditWorkSite, onDeleteWorkSite, getWorkSitesForProject, onSetPrimary, onShowWorkSiteMap }) => {
  const isCompact = useIsMobile(768);

  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No projects found</h3>
        <p className="text-sm text-gray-500">Get started by creating your first project</p>
      </div>
    );
  }

  const rowProps = (project) => ({
    project,
    workSites: getWorkSitesForProject(project.project_code),
    onEdit: () => onEditProject(project),
    onDelete: () => onDeleteProject(project),
    onView: () => onViewProject(project),
    onAddWorkSite: () => onAddWorkSite(project),
    onEditWorkSite,
    onDeleteWorkSite,
    onSetPrimary,
    onShowWorkSiteMap,
  });

  if (isCompact) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden divide-y divide-gray-100">
        {projects.map(project => (
          <ProjectRow key={project.project_code} view="mobile" {...rowProps(project)} />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table table-fixed w-full min-w-[800px]">
          <colgroup>
            <col className="w-[42%]" />
            <col className="w-[16%]" />
            <col className="w-[16%]" />
            <col className="w-[8%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead className="table-header">
            <tr>
              <th className="table-header-cell">Project Name</th>
              <th className="table-header-cell">Code</th>
              <th className="table-header-cell">Status</th>
              <th className="table-header-cell text-center">Sites</th>
              <th className="table-header-cell text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="table-body">
            {projects.map(project => (
              <ProjectRow key={project.project_code} view="desktop" {...rowProps(project)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Project Row Component (Accordion Parent)
const ProjectRow = ({
  project,
  workSites,
  onEdit,
  onDelete,
  onView,
  onAddWorkSite,
  onEditWorkSite,
  onDeleteWorkSite,
  onSetPrimary,
  onShowWorkSiteMap,
  view = 'desktop',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasWorkSites = workSites.length > 0;

  const actionButtons = (
    <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
      <button type="button" className="icon-button-admin" title="Add work site" onClick={onAddWorkSite}>
        <Plus className="w-4 h-4" />
      </button>
      <button type="button" className="icon-button-admin" title="Edit project" onClick={onEdit}>
        <Edit className="w-4 h-4" />
      </button>
      <button type="button" className="icon-button-admin" title="Delete project" onClick={onDelete}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  if (view === 'mobile') {
    return (
      <>
        <div
          className={`px-4 py-3 table-row ${hasWorkSites ? 'cursor-pointer' : ''}`}
          onClick={() => hasWorkSites && setIsExpanded(!isExpanded)}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              {hasWorkSites ? (
                <ChevronRight className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${
                  isExpanded ? 'rotate-90' : ''
                }`} />
              ) : (
                <div className="w-4 flex-shrink-0" />
              )}
              <Building className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {project.project_name}
                </p>
                <p className="text-xs text-gray-500 font-mono mt-0.5 truncate">
                  {project.project_code}
                </p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <StatusBadge status={project.project_status} />
                  <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                    <MapPin className="w-3 h-3 text-gray-400" />
                    {workSites.length} site{workSites.length === 1 ? '' : 's'}
                  </span>
                </div>
              </div>
            </div>
            {actionButtons}
          </div>
        </div>
        {isExpanded && workSites.map(site => (
          <WorkSiteRow
            key={site.id}
            view="mobile"
            site={site}
            onEdit={() => onEditWorkSite(site)}
            onDelete={() => onDeleteWorkSite(site)}
            onSetPrimary={() => onSetPrimary(site)}
            onShowMap={() => onShowWorkSiteMap(site)}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <tr
        className={`table-row ${hasWorkSites ? 'cursor-pointer' : ''}`}
        onClick={() => hasWorkSites && setIsExpanded(!isExpanded)}
      >
        <td className="table-cell">
          <div className="flex items-center gap-3 min-w-0">
            {hasWorkSites ? (
              <ChevronRight className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`} />
            ) : (
              <div className="w-4 flex-shrink-0" />
            )}
            <Building className="w-4 h-4 text-red-600 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-900 truncate" title={project.project_name}>
              {project.project_name}
            </span>
          </div>
        </td>
        <td className="table-cell">
          <span className="text-sm text-gray-500 font-mono truncate block" title={project.project_code}>
            {project.project_code}
          </span>
        </td>
        <td className="table-cell">
          <StatusBadge status={project.project_status} />
        </td>
        <td className="table-cell text-center">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {workSites.length}
          </span>
        </td>
        <td className="table-cell text-right">
          {actionButtons}
        </td>
      </tr>
      {isExpanded && workSites.map(site => (
        <WorkSiteRow
          key={site.id}
          view="desktop"
          site={site}
          onEdit={() => onEditWorkSite(site)}
          onDelete={() => onDeleteWorkSite(site)}
          onSetPrimary={() => onSetPrimary(site)}
          onShowMap={() => onShowWorkSiteMap(site)}
        />
      ))}
    </>
  );
};

// Work Site Row Component (Accordion Child)
const WorkSiteRow = ({ site, onEdit, onDelete, onSetPrimary, onShowMap, view = 'desktop' }) => {
  const hasCoordinates = site.site_latitude && site.site_longitude;

  const siteActions = (
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      {hasCoordinates && (
        <button type="button" className="icon-button-admin" title="View on map" onClick={onShowMap}>
          <Map className="w-4 h-4" />
        </button>
      )}
      {!site.site_is_primary && (
        <button type="button" className="icon-button-admin" title="Set as primary" onClick={onSetPrimary}>
          <CheckCircle className="w-4 h-4" />
        </button>
      )}
      <button type="button" className="icon-button-admin" title="Edit work site" onClick={onEdit}>
        <Edit className="w-4 h-4" />
      </button>
      <button type="button" className="icon-button-admin" title="Delete work site" onClick={onDelete}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  if (view === 'mobile') {
    return (
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1 pl-5">
            <div className="w-1 h-4 bg-red-300 rounded-full flex-shrink-0 mt-0.5" />
            <span className="flex-shrink-0 mt-0.5">{getSiteTypeIcon(site.site_type)}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm text-gray-900 truncate">{site.site_name}</span>
                {site.site_is_primary && (
                  <span className="badge badge-admin-primary text-xs flex-shrink-0">Primary</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-gray-500">{getSiteTypeLabel(site.site_type)}</span>
                {hasCoordinates ? (
                  <span className="badge badge-success text-xs">Mapped</span>
                ) : (
                  <span className="badge badge-secondary text-xs">No GPS</span>
                )}
              </div>
            </div>
          </div>
          {siteActions}
        </div>
      </div>
    );
  }

  return (
    <tr className="bg-gray-50 border-t border-gray-100">
      <td className="table-cell">
        <div className="flex items-center gap-3 pl-8 min-w-0">
          <div className="w-1 h-4 bg-red-300 rounded-full flex-shrink-0" />
          <span className="flex-shrink-0">{getSiteTypeIcon(site.site_type)}</span>
          <span className="text-sm text-gray-900 truncate" title={site.site_name}>{site.site_name}</span>
          {site.site_is_primary && (
            <span className="badge badge-admin-primary text-xs flex-shrink-0">Primary</span>
          )}
        </div>
      </td>
      <td className="table-cell">
        <span className="text-xs text-gray-500 truncate block">{getSiteTypeLabel(site.site_type)}</span>
      </td>
      <td className="table-cell">
        {hasCoordinates ? (
          <span className="badge badge-success text-xs">Mapped</span>
        ) : (
          <span className="badge badge-secondary text-xs">No GPS</span>
        )}
      </td>
      <td className="table-cell" />
      <td className="table-cell text-right">
        {siteActions}
      </td>
    </tr>
  );
};

// Project Details Modal Component
const ProjectDetailsModal = ({ project, workSites, initialTypeFilter, onClose, onEditProject, onAddWorkSite, onEditWorkSite, onDeleteWorkSite, onShowProjectMap, onShowWorkSiteMap, onSetPrimary }) => {
  const hasCoordinates = project.project_latitude && project.project_longitude;
  const workSitesWithCoordinates = workSites.filter(site => site.site_latitude && site.site_longitude);
  const [typeFilter, setTypeFilter] = useState(initialTypeFilter || '');
  const filteredWorkSites = typeFilter ? workSites.filter(ws => ws.site_type === typeFilter) : workSites;
  const availableTypes = [...new Set(workSites.map(ws => ws.site_type).filter(Boolean))];
  
  // Get coordinates data from tender API structure
  const projectCoordinates = project.coordinates || [];
  
  return (
    <div className="modal-overlay">
      <div className="modal-container modal-container-xl">
        <div className="modal-header">
          <h3 className="modal-title flex items-center">
            <Building className="w-8 h-8 mr-3 text-red-600" />
            {project.project_name}
          </h3>
          <button onClick={onClose} className="modal-close-admin" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="modal-content">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <Info className="w-4 h-4 mr-2 text-red-600" />
                Project Code
              </p>
              <p className="text-lg font-semibold text-gray-900">{project.project_code}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <AlertCircle className="w-4 h-4 mr-2 text-orange-500" />
                Status
              </p>
              <p className="text-lg font-semibold text-gray-900">{project.project_status.replace('_', ' ').toUpperCase()}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <User className="w-4 h-4 mr-2 text-green-500" />
                Client
              </p>
              <p className="text-lg font-semibold text-gray-900">{project.project_client_name || 'N/A'}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <Calendar className="w-4 h-4 mr-2 text-purple-500" />
                Start Date
              </p>
              <p className="text-lg text-gray-700">{project.project_start_date ? formatDate(project.project_start_date) : 'N/A'}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <Calendar className="w-4 h-4 mr-2 text-red-500" />
                End Date
              </p>
              <p className="text-lg text-gray-700">{project.project_end_date ? formatDate(project.project_end_date) : 'N/A'}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <CheckCircle className="w-4 h-4 mr-2 text-red-600" />
                Requires Check-in
              </p>
              <p className="text-lg text-gray-700">{project.project_requires_checkin ? 'Yes' : 'No'}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <Target className="w-4 h-4 mr-2 text-orange-500" />
                Geofence Radius
              </p>
              <p className="text-lg text-gray-700">{project.project_geofence_radius}m</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <p className="text-sm font-medium text-gray-900 flex items-center mb-2">
                <MapPin className="w-4 h-4 mr-2 text-red-600" />
                Coordinates
              </p>
              <p className="text-lg text-gray-700">
                {hasCoordinates 
                  ? `${formatCoordinate(project.project_latitude, 6)}, ${formatCoordinate(project.project_longitude, 6)}`
                  : 'No coordinates'
                }
              </p>
            </div>
          </div>

          {/* Description */}
          {project.project_description && (
            <div className="mb-6 p-4 bg-red-50 rounded-2xl">
              <h4 className="text-sm font-medium text-red-900 mb-2 flex items-center">
                <Info className="w-4 h-4 mr-2" />
                Description
              </h4>
              <p className="text-red-800">{project.project_description}</p>
            </div>
          )}

          {/* Tender API Coordinates Section */}
          {projectCoordinates.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-2xl">
              <h4 className="text-lg font-medium text-blue-900 mb-3 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                Project Locations ({projectCoordinates.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projectCoordinates.map((coord, index) => (
                  <div key={index} className="bg-white rounded-2xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-blue-900 flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {coord.reference_name || `Location ${index + 1}`}
                      </h5>
                      {coord.description && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          {coord.description}
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      {coord.latitude && coord.longitude && (
                        <div className="flex items-center p-2 bg-green-50 rounded-xl">
                          <span className="w-16 text-gray-500">GPS:</span>
                          <span className="font-mono text-green-700">
                            {formatCoordinate(coord.latitude, 6)}, {formatCoordinate(coord.longitude, 6)}
                          </span>
                        </div>
                      )}
                      
                      {coord.address && (
                        <div className="flex items-start p-2 bg-blue-50 rounded-xl">
                          <span className="w-16 text-gray-500 mt-1">Address:</span>
                          <span className="text-blue-700 flex-1">{coord.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Map Section */}
          {(hasCoordinates || workSitesWithCoordinates.length > 0) && (
            <div className="mb-6 p-4 bg-gray-50 rounded-2xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-red-600" />
                  Project Location & Work Sites
                </h4>
                <button
                  onClick={onShowProjectMap}
                  className="btn btn-admin-primary gap-2"
                >
                  <Map className="w-4 h-4" />
                  View Full Map
                </button>
              </div>
              
              {hasCoordinates ? (
                <MapComponent
                  latitude={project.project_latitude}
                  longitude={project.project_longitude}
                  title={`${project.project_name} - Preview`}
                  height="200px"
                  className="mb-2 rounded-xl"
                />
              ) : (
                <div className="bg-white rounded-2xl p-4 border-2 border-gray-200">
                  <div className="text-center">
                    <div className="text-4xl mb-2">📍</div>
                    <div className="text-sm text-gray-600">
                      <div className="text-gray-500">
                        No project coordinates set
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Work Sites Summary */}
              <div className="text-center text-sm text-gray-600 mt-2">
                {workSitesWithCoordinates.length > 0 
                  ? `${workSitesWithCoordinates.length} of ${workSites.length} work sites mapped`
                  : 'No work sites mapped yet'
                }
              </div>
            </div>
          )}

          <div className="flex justify-end gap-4 mb-8">
            <button
              onClick={onEditProject}
              className="btn btn-admin-primary btn-lg gap-3"
            >
              <Edit className="w-5 h-5" />
              Edit Project
            </button>
            <button
              onClick={onAddWorkSite}
              className="btn btn-success btn-lg gap-3"
            >
              <Plus className="w-5 h-5" />
              Add Work Site
            </button>
          </div>

          <div className="mt-8 mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <h3 className="text-xl font-bold text-gray-900 flex items-center min-w-0">
              <Target className="w-6 h-6 mr-2 text-purple-600 flex-shrink-0" />
              <span className="truncate">
                Work Sites ({filteredWorkSites.length}{typeFilter ? ` of ${workSites.length}` : ''})
              </span>
            </h3>
            {availableTypes.length > 1 && (
              <PillToggle
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { id: '', label: 'All', title: 'Show all' },
                  ...availableTypes.map(t => ({
                    id: t,
                    label: t.replace('_', ' ').toUpperCase(),
                    title: `Show ${t}`,
                  }))
                ]}
                variant="text"
                theme="admin"
                layout="sectionHeader"
                ariaLabel="Filter work sites by type"
              />
            )}
          </div>
          {filteredWorkSites.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-2xl">
              <div className="text-4xl mb-2">📍</div>
              <p className="text-gray-500 mb-4">No work sites yet for this project.</p>
              <button
                onClick={onAddWorkSite}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-2xl hover:bg-purple-700 transition-colors text-sm font-medium shadow-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Work Site
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredWorkSites.map(workSite => (
                <WorkSiteCard
                  key={workSite.id}
                  workSite={workSite}
                  onEdit={() => onEditWorkSite(workSite)}
                  onDelete={() => onDeleteWorkSite(workSite)}
                  onShowMap={() => onShowWorkSiteMap(workSite)}
                  onSetPrimary={() => onSetPrimary(workSite)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Work Site Card Component
const WorkSiteCard = ({ workSite, onEdit, onDelete, onShowMap, onSetPrimary }) => {
  const colorConfig = getSiteTypeColor(workSite.site_type);
  const friendlyTypeLabel = getSiteTypeLabel(workSite.site_type);

  const hasCoordinates = workSite.site_latitude && workSite.site_longitude;

  return (
    <div className="card card-hover p-6 bg-white hover:shadow-xl transition-all duration-300 border-2 border-gray-100">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-xl font-bold text-gray-900 flex items-center">
          {getSiteTypeIcon(workSite.site_type)}
          <span className="ml-3">{workSite.site_name}</span>
        </h3>
        {workSite.site_is_primary && (
          <span className="badge badge-admin-primary gap-2">
            <CheckCircle className="w-3 h-3" />
            Primary
          </span>
        )}
      </div>

      <p className="text-base text-gray-600 mb-3 flex items-center">
        <Info className="w-4 h-4 mr-2" />
        {workSite.project_code}
      </p>
      
      <span className={`badge badge-lg gap-2 mb-4 ${colorConfig.badge}`}>
        {getSiteTypeIcon(workSite.site_type)}
        <span>{friendlyTypeLabel}</span>
      </span>

      {workSite.site_description && (
        <p className="text-sm text-gray-700 mb-3 line-clamp-2">{workSite.site_description}</p>
      )}

      {/* Location (compact, intuitive) */}
      <div className="mb-4 p-4 bg-gray-50 rounded-2xl border border-gray-200 relative">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {hasCoordinates ? (
            <span className="badge badge-success gap-1" title="Work site GPS coordinates">
              <Navigation className="w-3 h-3" />
              GPS: {formatCoordinate(workSite.site_latitude)}, {formatCoordinate(workSite.site_longitude)}
            </span>
          ) : (
            <span className="badge badge-secondary gap-1" title="Work site GPS not set">
              <MapPin className="w-3 h-3" />
              GPS: not set
            </span>
          )}
          <span className="badge badge-info gap-1" title="Work site geofence radius">
            <Target className="w-3 h-3" />
            Geofence: {workSite.site_geofence_radius}m
          </span>
        </div>
        
        {/* View Map Button - Repositioned for better visibility */}
        {hasCoordinates && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={onShowMap}
              className="icon-button-admin"
              title="View work site map"
              aria-label="View work site map"
            >
              <MapPin className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-100">
        {!workSite.site_is_primary && (
          <button
            onClick={onSetPrimary}
            className="icon-button-admin"
            title="Set as primary"
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onEdit}
          className="icon-button-admin"
          title="Edit work site"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="icon-button-admin"
          title="Delete work site"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Map Modal Component
const MapModal = ({ projects, workSites, onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-container modal-container-lg">
        <div className="modal-header">
          <h3 className="modal-title flex items-center">
            <Map className="w-7 h-7 mr-3 text-red-600" />
            Map View
          </h3>
          <button onClick={onClose} className="modal-close-admin" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="modal-content">
          <div className="bg-gray-50 rounded-3xl p-8 text-center">
            <Map className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Interactive Map</h3>
            <p className="text-gray-500 mb-4">
              This feature is under development. You can view project and work site locations here.
            </p>
            <div className="bg-white rounded-2xl p-6 border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-600">
                Map integration with Singapore coordinates coming soon...
              </p>
              <div className="mt-4 text-xs text-gray-500 flex justify-center gap-6">
                <p className="flex items-center">
                  <Building className="w-4 h-4 mr-1" />
                  {projects.length} projects
                </p>
                <p className="flex items-center">
                  <Target className="w-4 h-4 mr-1" />
                  {workSites.length} work sites
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Project Map Modal Component
const ProjectMapModal = ({ project, workSites, selectedCoordinate, onClose }) => {
  const workSitesWithCoordinates = workSites.filter(site => site.site_latitude && site.site_longitude);
  
  // Auto-select primary work site or first work site with coordinates
  const getDefaultWorkSite = () => {
    const primarySite = workSitesWithCoordinates.find(site => site.site_is_primary);
    return primarySite || workSitesWithCoordinates[0] || null;
  };
  
  const [selectedWorkSite, setSelectedWorkSite] = useState(getDefaultWorkSite);
  
  // Get coordinates data from tender API structure
  const projectCoordinates = project.coordinates || [];

  // Determine what to show on the map
  const getMapTitle = () => {
    if (selectedWorkSite) {
      return selectedWorkSite.site_name;
    }
    if (selectedCoordinate) {
      return `${selectedCoordinate.reference_name || 'Location'}`;
    }
    return 'No Location Available';
  };

  const getMapSubtitle = () => {
    if (selectedWorkSite) {
      return `Interactive map showing ${selectedWorkSite.site_name} location`;
    }
    if (selectedCoordinate) {
      return `Interactive map showing ${selectedCoordinate.reference_name || 'location'} coordinates`;
    }
    return 'Select a work site to view on map';
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container" style={{ maxWidth: '95vw', width: '95vw', maxHeight: '95vh' }}>
        <div className="modal-header">
          <h3 className="modal-title flex items-center">
            <Map className="w-7 h-7 mr-3 text-red-600" />
            {project.project_name}
          </h3>
          <button onClick={onClose} className="modal-close-admin" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="modal-content" style={{ maxHeight: 'calc(95vh - 120px)', overflowY: 'auto' }}>
          {/* Map Container */}
          <div className="mb-4">
            {selectedWorkSite ? (
              // Show selected work site map with unique key to force remount
              <MapComponent
                key={`worksite-map-${selectedWorkSite.id}`}
                latitude={selectedWorkSite.site_latitude}
                longitude={selectedWorkSite.site_longitude}
                title={`${selectedWorkSite.site_name} - ${project.project_name}`}
                height="400px"
                className="mb-3 rounded-2xl"
              />
            ) : selectedCoordinate ? (
              // Show specific coordinate map
              <MapComponent
                key={`coord-map-${selectedCoordinate.latitude}-${selectedCoordinate.longitude}`}
                latitude={selectedCoordinate.latitude}
                longitude={selectedCoordinate.longitude}
                title={`${selectedCoordinate.reference_name || 'Location'} - ${project.project_name}`}
                height="400px"
                className="mb-3 rounded-2xl"
              />
            ) : (
              <div className="bg-gray-50 rounded-2xl p-6 text-center">
                <div className="text-3xl mb-2">📍</div>
                <h4 className="text-base font-medium text-gray-900 mb-1">No Work Sites Available</h4>
                <p className="text-sm text-gray-500">
                  Add work sites with GPS coordinates to view them on the map
                </p>
              </div>
            )}
            
            {/* Work Sites Selector - Streamlined tab-style */}
            {workSitesWithCoordinates.length > 0 && (
              <div className="mt-3 p-2.5 bg-gray-100 rounded-xl">
                <div className="flex items-center gap-2 flex-wrap">
                  {workSitesWithCoordinates.map(site => (
                    <button
                      key={site.id}
                      onClick={() => setSelectedWorkSite(site)}
                      className={`flex items-center text-sm px-3 py-1.5 rounded-lg cursor-pointer transition-all font-medium ${
                        selectedWorkSite?.id === site.id 
                          ? 'bg-red-600 text-white shadow-md' 
                          : 'text-gray-700 hover:text-gray-900'
                      }`}
                      title={`View ${site.site_name} on map`}
                    >
                      <MapPin className={`w-3.5 h-3.5 mr-1.5 ${selectedWorkSite?.id === site.id ? 'text-white' : 'text-red-600'}`} />
                      <span className="truncate">{site.site_name}</span>
                      {site.site_is_primary && (
                        <span className={`ml-1.5 text-xs ${selectedWorkSite?.id === site.id ? 'text-red-200' : 'text-red-600'}`}>★</span>
                      )}
                    </button>
                  ))}
                  {workSites.length > workSitesWithCoordinates.length && (
                    <span className="text-xs text-gray-500 px-2">
                      +{workSites.length - workSitesWithCoordinates.length} without coordinates
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tender API Coordinates Section - Compact */}
          {projectCoordinates.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2.5 flex items-center">
                <MapPin className="w-4 h-4 mr-1.5 text-blue-600" />
                Tender API Locations ({projectCoordinates.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {projectCoordinates.map((coord, index) => (
                  <div key={index} className="bg-white rounded-lg p-2.5 border border-blue-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-xs font-medium text-blue-900 flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        <span className="truncate">{coord.reference_name || `Location ${index + 1}`}</span>
                      </h5>
                      {coord.description && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full ml-1 flex-shrink-0">
                          {coord.description}
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1.5 text-xs">
                      {coord.latitude && coord.longitude ? (
                        <div className="flex items-center p-1.5 bg-green-50 rounded-lg">
                          <span className="w-12 text-gray-500 text-xs">GPS:</span>
                          <span className="font-mono text-green-700 text-xs">
                            {formatCoordinate(coord.latitude, 6)}, {formatCoordinate(coord.longitude, 6)}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center p-1.5 bg-gray-50 rounded-lg">
                          <span className="w-12 text-gray-500 text-xs">GPS:</span>
                          <span className="text-gray-400 text-xs">Not set</span>
                        </div>
                      )}
                      
                      {coord.address && (
                        <div className="flex items-start p-1.5 bg-blue-50 rounded-lg">
                          <span className="w-12 text-gray-500 text-xs mt-0.5">Addr:</span>
                          <span className="text-blue-700 flex-1 text-xs leading-tight">{coord.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// Work Site Map Modal Component
const WorkSiteMapModal = ({ workSite, onClose }) => {
  const hasCoordinates = workSite.site_latitude && workSite.site_longitude;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-container-lg">
        <div className="modal-header">
          <h3 className="modal-title flex items-center">
            <MapPin className="w-7 h-7 mr-3 text-purple-600" />
            {workSite.site_name}
          </h3>
          <button onClick={onClose} className="modal-close-admin" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="modal-content">
          {/* Compact Info Bar - Horizontal */}
          <div className="flex items-center gap-3 flex-wrap mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
            <span className="badge badge-info text-xs flex items-center gap-1.5">
              <Factory className="w-3 h-3" />
              {workSite.site_type.replace('_', ' ').toUpperCase()}
            </span>
            {workSite.site_is_primary && (
              <span className="badge badge-admin-primary text-xs flex items-center gap-1.5">
                <CheckCircle className="w-3 h-3" />
                Primary
              </span>
            )}
            {hasCoordinates && (
              <>
                <span className="badge badge-success text-xs flex items-center gap-1.5">
                  <Navigation className="w-3 h-3" />
                  {formatCoordinate(workSite.site_latitude, 6)}, {formatCoordinate(workSite.site_longitude, 6)}
                </span>
                <span className="badge badge-warning text-xs flex items-center gap-1.5" title={`Employees must be within ${workSite.site_geofence_radius}m to clock in/out`}>
                  <Target className="w-3 h-3" />
                  {workSite.site_geofence_radius}m geofence
                </span>
              </>
            )}
            {!hasCoordinates && (
              <span className="badge badge-secondary text-xs flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" />
                No coordinates
              </span>
            )}
          </div>

          {/* Map Container */}
          <div className="mb-4">
            {hasCoordinates ? (
              <MapComponent
                latitude={workSite.site_latitude}
                longitude={workSite.site_longitude}
                title={`${workSite.site_name} - Work Site`}
                height="500px"
                className="rounded-2xl"
              />
            ) : (
              <div className="bg-gray-50 rounded-2xl p-6 text-center">
                <div className="text-3xl mb-2">📍</div>
                <h4 className="text-base font-medium text-gray-900 mb-1">No Work Site Coordinates</h4>
                <p className="text-sm text-gray-500">
                  Add coordinates to view on map and enable geofencing
                </p>
              </div>
            )}
          </div>

          {/* Compact Description */}
          {workSite.site_description && (
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700 flex items-start">
                <Info className="w-3 h-3 mr-1.5 mt-0.5 text-gray-500 flex-shrink-0" />
                <span>{workSite.site_description}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Work Site Form Modal Component
const WorkSiteFormModal = ({ projectCode, initialData, onSubmit, onCancel }) => {
  // Amend only when we have a persisted site id. Draft defaults (quick-add) must create.
  const isEditMode = initialData?.id != null && initialData.id !== '';
  const resolvedProjectCode =
    projectCode ||
    initialData?.project_code ||
    (typeof initialData?.project === 'string' ? initialData.project : '') ||
    '';

  const [formData, setFormData] = useState(() => {
    const base = {
      site_name: '',
      site_description: '',
      site_address: '',
      site_postal_code: '',
      unit_number: '',
      site_latitude: '',
      site_longitude: '',
      site_geofence_radius: 150,
      site_type: 'project_site',
      site_status: 'active',
      site_requires_checkin: true,
      site_is_primary: false,
      site_access_instructions: '',
      site_safety_requirements: '',
      ...(initialData || {}),
    };
    return {
      ...base,
      // Always prefer the resolved project code over any stale nested project object.
      project: resolvedProjectCode || (typeof base.project === 'string' ? base.project : '') || '',
      // Only carry id when amending an existing site.
      id: isEditMode ? initialData.id : undefined,
    };
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [radiusManuallyEdited, setRadiusManuallyEdited] = useState(isEditMode);

  // Suggested geofence radius per site type (meters)
  const suggestedRadiusByType = {
    project_site: 200,
    site_office: 150,
    office: 100,
    dfma_factory: 200,
    factory: 200,
    storage: 150,
    dormitory: 150,
  };

  // When site type changes, suggest a radius (only if not manually edited)
  const handleSiteTypeChange = (newType) => {
    const updates = { site_type: newType };
    if (!radiusManuallyEdited && suggestedRadiusByType[newType]) {
      updates.site_geofence_radius = suggestedRadiusByType[newType];
    }
    setFormData({ ...formData, ...updates });
  };

  // Handle manual coordinate input
  const handleCoordinateChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const projectValue = formData.project || resolvedProjectCode;
      // Validate required fields
      const newErrors = {};
      if (!formData.site_name.trim()) {
        newErrors.site_name = 'Site name is required';
      }
      if (!projectValue) {
        newErrors.project = 'Project is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setIsSubmitting(false);
        return;
      }

      // Clean up the data — keep id so parent can PUT the same row
      const cleanedData = {
        id: isEditMode ? formData.id : undefined,
        project: projectValue,
        project_code: projectValue,
        site_name: formData.site_name.trim(),
        site_latitude: formData.site_latitude ? parseFloat(formData.site_latitude) : null,
        site_longitude: formData.site_longitude ? parseFloat(formData.site_longitude) : null,
        site_geofence_radius: parseInt(formData.site_geofence_radius, 10) || 150,
        site_type: formData.site_type,
        site_status: formData.site_status,
        site_requires_checkin: Boolean(formData.site_requires_checkin),
        site_is_primary: Boolean(formData.site_is_primary),
        site_postal_code: (formData.site_postal_code || '').trim() || '',
        site_description: (formData.site_description || '').trim() || null,
        site_address: (formData.site_address || '').trim() || null,
        unit_number: (formData.unit_number || '').trim() || null,
        site_access_instructions: (formData.site_access_instructions || '').trim() || null,
        site_safety_requirements: (formData.site_safety_requirements || '').trim() || null,
      };

      await onSubmit(cleanedData);
    } catch (error) {
      console.error('Error submitting work site:', error);
      toast.error('Failed to save work site');
    } finally {
      setIsSubmitting(false);
    }
  };

  const siteTypes = siteTypeOptions;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-container-lg max-h-[85vh]">
        <div className="modal-header">
          <h3 className="modal-title flex items-center gap-2">
            {isEditMode ? (
              <Edit className="w-6 h-6 text-red-600" />
            ) : (
              <Plus className="w-6 h-6 text-red-600" />
            )}
            {isEditMode ? 'Edit Work Site' : 'Add Work Site'}
          </h3>
          <button onClick={onCancel} className="modal-close-admin" title="Close" type="button">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="modal-content pr-2">
          <p className="text-sm text-gray-600 mt-1 flex items-center mb-4">
            <Building className="w-4 h-4 mr-2 text-red-600" />
            Project: <span className="ml-1 font-mono text-gray-900">{resolvedProjectCode || '—'}</span>
          </p>
          {errors.project && (
            <p className="text-red-600 text-sm mb-4 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.project}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label-admin flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-red-600" />
                  Site Name *
                </label>
                <input
                  type="text"
                  value={formData.site_name}
                  onChange={(e) => setFormData({...formData, site_name: e.target.value})}
                  className={`input-admin ${
                    errors.site_name ? 'input-error' : ''
                  }`}
                  placeholder="Enter site name"
                  required
                />
                {errors.site_name && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {errors.site_name}
                  </p>
                )}
              </div>

              <div>
                <label className="form-label-admin flex items-center">
                  <Factory className="w-4 h-4 mr-2 text-orange-500" />
                  Site Type
                </label>
                <FormSelect
                  value={formData.site_type}
                  onChange={(val) => handleSiteTypeChange(val)}
                  options={siteTypes.map(type => ({ value: type.value, label: type.label }))}
                  theme="admin"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="form-label-admin flex items-center">
                <Info className="w-4 h-4 mr-2 text-red-600" />
                Description
              </label>
              <input
                type="text"
                value={formData.site_description || ''}
                onChange={(e) => setFormData({...formData, site_description: e.target.value})}
                className="input-admin"
                placeholder="Brief description of the work site"
              />
            </div>

            {/* Address and Postal Code Lookup */}
            <div className="space-y-6">
              {/* Postal Code Lookup */}
              <div>
                <PostalCodeLookup
                  value={formData.site_postal_code}
                  onAddressFound={(geocodedData) => {
                    setFormData({
                      ...formData,
                      site_address: geocodedData.address,
                      site_postal_code: geocodedData.postal_code || '',
                      site_latitude: geocodedData.latitude,
                      site_longitude: geocodedData.longitude
                    });

                    // Clear any previous address-related errors
                    setErrors({
                      ...errors,
                      site_address: '',
                      site_latitude: '',
                      site_longitude: ''
                    });

                    toast.success('📍 Work site address auto-filled!');
                  }}
                  onPostalCodeChange={(newPostalCode) => {
                    setFormData({ ...formData, site_postal_code: newPostalCode });
                  }}
                  onError={(error) => toast.error(error)}
                  placeholder="Enter postal code to auto-fill address (e.g., 018989)"
                  buttonText="Look Up Address"
                  isAdmin={true}
                />
              </div>

              <div>
                <label className="form-label-admin">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Site Address
                </label>
                <input
                  type="text"
                  value={formData.site_address || ''}
                  onChange={(e) => setFormData({...formData, site_address: e.target.value})}
                  className="input-admin"
                  placeholder="Full address of the work site (auto-filled from postal code lookup)"
                />
              </div>

              <div>
                <label className="form-label-admin">
                  <Home className="w-4 h-4 inline mr-2" />
                  Unit Number
                </label>
                <input
                  type="text"
                  value={formData.unit_number || ''}
                  onChange={(e) => setFormData({...formData, unit_number: e.target.value})}
                  className="input-admin"
                  placeholder="e.g., #12-34, Unit 5, Level 3"
                />
                <p className="form-help">
                  Optional: Floor/unit number for complete address
                </p>
              </div>
            </div>

            {/* Coordinates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label-admin">
                  <Navigation className="w-4 h-4 inline mr-2" />
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.site_latitude ?? ''}
                  onChange={(e) => handleCoordinateChange('site_latitude', e.target.value)}
                  className="input-admin"
                  placeholder="1.3521 (auto-filled)"
                />
              </div>

              <div className="form-group">
                <label className="form-label-admin">
                  <Navigation className="w-4 h-4 inline mr-2" />
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.site_longitude ?? ''}
                  onChange={(e) => handleCoordinateChange('site_longitude', e.target.value)}
                  className="input-admin"
                  placeholder="103.8198 (auto-filled)"
                />
              </div>
            </div>

            {/* Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label-admin">
                  <Target className="w-4 h-4 inline mr-2" />
                  Geofence Radius (meters)
                </label>
                <input
                  type="number"
                  value={formData.site_geofence_radius}
                  onChange={(e) => {
                    setRadiusManuallyEdited(true);
                    setFormData({...formData, site_geofence_radius: e.target.value});
                  }}
                  className="input-admin"
                  min="50"
                  max="500"
                  step="50"
                  placeholder="150"
                />
                <p className="form-help">
                  Detection radius for automatic check-in (50-500 meters, default: 150m)
                </p>
                {suggestedRadiusByType[formData.site_type] && (
                  <p className="text-xs text-blue-600 mt-1">
                    Suggested for {formData.site_type.replace(/_/g, ' ')}: {suggestedRadiusByType[formData.site_type]}m
                    {!radiusManuallyEdited && ' (auto-applied)'}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label-admin">
                  <Settings className="w-4 h-4 inline mr-2" />
                  Status
                </label>
                <FormSelect
                  value={formData.site_status}
                  onChange={(val) => setFormData({...formData, site_status: val})}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                    { value: 'planned', label: 'Planned' },
                    { value: 'completed', label: 'Completed' },
                  ]}
                  theme="admin"
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center p-4 bg-red-50 rounded-2xl">
                <input
                  type="checkbox"
                  id="requires_checkin"
                  checked={formData.site_requires_checkin}
                  onChange={(e) => setFormData({...formData, site_requires_checkin: e.target.checked})}
                  className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded-xl focus-visible:ring-red-500"
                />
                <label htmlFor="requires_checkin" className="ml-3 text-sm text-red-900 font-medium flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Requires check-in
                </label>
              </div>

              <div className="flex items-center p-4 bg-purple-50 rounded-2xl">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={formData.site_is_primary}
                  onChange={(e) => setFormData({...formData, site_is_primary: e.target.checked})}
                  className="w-5 h-5 text-purple-600 bg-gray-100 border-gray-300 rounded-xl focus-visible:ring-purple-500"
                />
                <label htmlFor="is_primary" className="ml-3 text-sm text-purple-900 font-medium flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Primary site
                </label>
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label-admin flex items-center">
                  <Info className="w-4 h-4 mr-2 text-red-600" />
                  Access Instructions
                </label>
                <input
                  type="text"
                  value={formData.site_access_instructions || ''}
                  onChange={(e) => setFormData({...formData, site_access_instructions: e.target.value})}
                  className="input-admin"
                  placeholder="How to access this site..."
                />
              </div>

              <div>
                <label className="form-label-admin flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2 text-red-600" />
                  Safety Requirements
                </label>
                <input
                  type="text"
                  value={formData.site_safety_requirements || ''}
                  onChange={(e) => setFormData({...formData, site_safety_requirements: e.target.value})}
                  className="input-admin"
                  placeholder="Safety requirements for this site..."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-8 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="btn btn-admin-outline gap-2"
                disabled={isSubmitting}
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-admin-primary btn-lg gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    {isEditMode ? 'Update Work Site' : 'Create Work Site'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SiteManagement; 